import * as React from 'react';
import type { JSX } from 'react';

interface TextHighlightProps {
  text: string;
  searchTerm: string;
  className?: string;
  highlightClassName?: string;
}

export function TextHighlight({
  text,
  searchTerm,
  className = '',
  highlightClassName = 'bg-glitter-200 font-semibold text-steel-500',
}: TextHighlightProps): JSX.Element {
  if (!searchTerm || !text) {
    return <span className={className}>{text}</span>;
  }

  // Escape special regex characters and create case-insensitive regex
  const escapedTerm = searchTerm.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  // Find all matches first
  const matches = Array.from(text.matchAll(new RegExp(escapedTerm, 'gi')));

  if (matches.length === 0) {
    return <span className={className}>{text}</span>;
  }

  let lastOffset = 0;
  const result: React.ReactNode[] = [];

  matches.forEach((match) => {
    const matchStart = match.index!;
    const matchEnd = matchStart + match[0].length;

    // Add text before match
    if (matchStart > lastOffset) {
      result.push(text.slice(lastOffset, matchStart));
    }

    // Add highlighted match
    result.push(
      <span key={`match-${matchStart}-${match[0].slice(0, 10)}`} className={highlightClassName}>
        {match[0]}
      </span>,
    );

    lastOffset = matchEnd;
  });

  // Add remaining text after last match
  if (lastOffset < text.length) {
    result.push(text.slice(lastOffset));
  }

  return <span className={className}>{result}</span>;
}
